/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { api, ApiError } from "core/api";
import { Button, Card, Input, Form, FieldGroup, Select, Switch } from "core/components";
import { navigateTo } from "core/navigation";
import {
  CostItemFormData,
  CostItemSchema,
  CostTypeMask,
  CostTypeMaxLength,
  ItemCategoryFormData,
  ItemSubcategoryFormData,
  UnitMeasureFormData
} from "core/schema";
import { Option } from "core/types";
import { admin_cost_items_path, admin_cost_item_path } from "routes";
import { IMaskInput } from "react-imask";

type CostItemFormProps = {
  cost_item: CostItemFormData;
  item_categories: Array<ItemCategoryFormData>;
  item_subcategories: Array<ItemSubcategoryFormData>;
  unit_measures: Array<UnitMeasureFormData>;
  tax_categories: Array<Option>;
};

const CostItemForm = ({
  cost_item,
  item_categories,
  item_subcategories,
  unit_measures,
  tax_categories,
  accounts
}: CostItemFormProps): JSX.Element => {
  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting, errors }
  } = useForm<CostItemFormData>({
    mode: "onChange",
    defaultValues: CostItemSchema.cast(cost_item),
    resolver: yupResolver(CostItemSchema)
  });
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);

  const save = async (data: CostItemFormData): Promise<unknown> => {
    try {
      if (data.id) {
        await api.put(admin_cost_item_path(cost_item), data);
      } else {
        await api.post(admin_cost_items_path(cost_item), data);
      }

      navigateTo(admin_cost_items_path({ item_type: cost_item.item_type }));
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  return (
    <React.StrictMode>
      <Form errors={formErrors}>
        <Card>
          <Card.Header title={cost_item.id ? `Edit Cost Item` : `New Cost Item`}>
            <Button
              icon="arrow-left"
              iconProps={{ className: "mr-3" }}
              className="button--default mr-3"
              disabled={isSubmitting}
              title="Back"
              onClick={() => navigateTo(admin_cost_items_path({ item_type: cost_item.item_type }))}
            />
            <Button
              icon="save"
              iconProps={{ className: "mr-3" }}
              className="button--brand mr-3"
              disabled={isSubmitting}
              title="Save"
              onClick={handleSubmit(async (data) => save(data))}
            />
          </Card.Header>
          <Card.Body>
            <Container>
              <Row>
                <Col lg={6} className="px-4">
                  <Controller
                    name="active"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        label="Active"
                        info="Active or Inactive"
                        checked={field.value}
                        value={field.value?.toString()}
                        errorMessage={errors.active?.message}
                      />
                    )}
                  />
                  <Controller
                    name="item_category_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Cost Type"
                        placeholder="Select Cost Type"
                        required
                        containerStyle={{ paddingTop: "1rem" }}
                        options={item_categories}
                        getOptionLabel={(option) => `${option.code} - ${option.name}`}
                        getOptionValue={(option) => option.id}
                        isOptionDisabled={(option) => !option.active}
                        onChange={(newOption) => {
                          field.onChange(newOption?.id);
                          setValue("item_subcategory_id", null);
                        }}
                        value={item_categories.find((item_category) => item_category.id == field.value) || null}
                        errorMessage={errors.item_category_id?.message}
                      />
                    )}
                  />
                  <Controller
                    name="item_subcategory_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Cost Subtype"
                        placeholder="Select Cost Subtype"
                        noOptionsMessage={() => "Select a Cost Type first."}
                        required
                        options={item_subcategories?.filter((item_subcategory) => item_subcategory.item_category_id == watch("item_category_id"))}
                        getOptionLabel={(option) => `${option.code} - ${option.name}`}
                        getOptionValue={(option) => option.id}
                        isOptionDisabled={(option) => !option.active}
                        onChange={(newOption) => {
                          field.onChange(newOption?.id);
                        }}
                        value={item_subcategories.find((item_subcategory) => item_subcategory.id == field.value) || null}
                        errorMessage={errors.item_subcategory_id?.message}
                      />
                    )}
                  />
                  <Controller
                    name="code"
                    control={control}
                    render={({ field }) => (
                      <FieldGroup label="Code" required errorMessage={errors.code?.message}>
                        <IMaskInput
                          mask={CostTypeMask}
                          value={field.value}
                          unmask={false}
                          inputRef={field.ref}
                          onAccept={(value) => {
                            field.onChange(value);
                          }}
                          maxLength={CostTypeMaxLength}
                          className="field text-uppercase"
                          placeholder="COS10000"
                        />
                      </FieldGroup>
                    )}
                  />
                </Col>
                <Col lg={6} className="px-4">
                  <Input {...register("name")} label="Name" required errorMessage={errors.name?.message} placeholder="Labor" maxLength={100} />
                  <Row>
                    <Col>
                      <Controller
                        name="unit_measure_id"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            label="Unit of Measure"
                            placeholder="Select Unit of Measure"
                            required
                            options={unit_measures}
                            getOptionLabel={(option) => `${option.name} (${option.code})`}
                            getOptionValue={(option) => option.id}
                            isOptionDisabled={(option) => !option.active}
                            onChange={(newOption) => {
                              field.onChange(newOption?.id);
                            }}
                            value={unit_measures?.find((unit_measure) => unit_measure.id == field.value) || null}
                            errorMessage={errors.unit_measure_id?.message}
                          />
                        )}
                      />
                    </Col>
                    <Col>
                      <Controller
                        name="standard_price"
                        control={control}
                        render={({ field }) => (
                          <FieldGroup label="Standard Price" required errorMessage={errors.standard_price?.message}>
                            <div className="d-flex">
                              <span
                                className="field"
                                style={{
                                  flex: 1,
                                  alignItems: "center",
                                  fontWeight: "bold",
                                  backgroundColor: "#e9ecef",
                                  border: "1px solid #ced4da",
                                  whiteSpace: "nowrap",
                                  paddingTop: "10px",
                                  borderTopRightRadius: 0,
                                  borderBottomRightRadius: 0
                                }}
                              >
                                ₱
                              </span>
                              <IMaskInput
                                mask={Number}
                                padFractionalZeros={true}
                                thousandsSeparator=","
                                radix="."
                                value={field.value?.toString()}
                                unmask={true}
                                inputRef={field.ref}
                                onAccept={(value) => {
                                  field.onChange(value);
                                }}
                                className="field"
                                placeholder="1000.00"
                                min={0}
                                max={9999999}
                              />
                            </div>
                          </FieldGroup>
                        )}
                      />
                    </Col>
                  </Row>
                  <Controller
                    name="account_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Default GL Account"
                        placeholder="Select GL Account"
                        required
                        options={accounts}
                        getOptionLabel={(option) => `${option.code} - ${option.name}`}
                        getOptionValue={(option) => option.id}
                        isOptionDisabled={(option) => !option.active}
                        noOptionsMessage={() => "No GL Accounts defined."}
                        onChange={(newOption) => {
                          field.onChange(newOption?.id);
                        }}
                        value={accounts.find((account) => account.id == field.value) || null}
                        errorMessage={errors.account_id?.message}
                      />
                    )}
                  />
                  <Row>
                    <Col>
                      <Controller
                        name="tax_category"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            label="Tax Category"
                            placeholder="Select Tax Category"
                            required
                            options={tax_categories}
                            getOptionLabel={(option) => option.label}
                            getOptionValue={(option) => option.value}
                            onChange={(newOption) => {
                              field.onChange(newOption?.value);
                            }}
                            value={tax_categories?.find((tax_category) => tax_category.value == field.value) || null}
                            errorMessage={errors.tax_category?.message}
                          />
                        )}
                      />
                    </Col>
                    <Col>
                      <Controller
                        name="withholding_tax_rate"
                        control={control}
                        render={({ field }) => (
                          <FieldGroup label="Withholding Tax Rate" required errorMessage={errors.withholding_tax_rate?.message}>
                            <IMaskInput
                              mask="0.00"
                              padFractionalZeros={true}
                              value={field.value?.toString()}
                              unmask={false}
                              inputRef={field.ref}
                              onAccept={(value) => {
                                field.onChange(value);
                              }}
                              className="field"
                              placeholder="0.01"
                              min={0}
                            />
                          </FieldGroup>
                        )}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Container>
          </Card.Body>
        </Card>
      </Form>
    </React.StrictMode>
  );
};

export default CostItemForm;
